/**
 * Core builder for FHIR AuditEvent
 * @param {object} params - includes action, subtype, agent, entity, description, outcome, store
 * @returns {object} FHIR AuditEvent
 */
export function buildAuditEvent({ action, subtype, agent, entity, description, outcome = "0", store }) {
  const orgConfig = store?.getState().config?.orgConfig || {};
  const fhirServer = orgConfig?.iss || "unknown-server";

  return {
    resourceType: "AuditEvent",
    type: {
      system: "http://terminology.hl7.org/CodeSystem/audit-event-type",
      code: "rest",
      display: "RESTful Operation"
    },
    subtype: [subtype],
    action,
    recorded: new Date().toISOString(),
    outcome,
    description,
    source: {
      site: orgConfig?.name || "unknown-org",
      observer: { reference: fhirServer }
    },
    agent: [agent],
    entity: entity ? [entity] : []
  };
}

/**
 * Build a FHIR AuditEvent for SMART login / data access
 * @param {object} params - includes practitioner, patient, outcome, store
 * @returns {object} FHIR AuditEvent
 */
export function buildLoginAuditEvent({ practitioner, patient, outcome = "0", store }) {
  return buildAuditEvent({
    action: "L",
    subtype: {
      system: "http://hl7.org/fhir/restful-interaction",
      code: "login",
      display: "Login"
    },
    agent: {
      who: {
        reference: `Practitioner/${practitioner?.id || "unknown"}`,
        display: `${practitioner?.name?.[0]?.given?.join(" ") || ""} ${practitioner?.name?.[0]?.family || ""}`.trim() || "Unknown"
      },
      requestor: true
    },
    entity: {
      what: {
        reference: `Patient/${patient?.id || "unknown"}`,
        display: patient?.name?.[0]?.text || "Unknown"
      },
      role: { code: "1", display: "Patient" }
    },
    description: "SMART-on-FHIR login",
    outcome,
    store
  });
}

/**
 * Build a FHIR AuditEvent for sending a Communication
 * @param {object} params - includes communication, outcome, store
 * @returns {object} FHIR AuditEvent
 */
export function buildCommunicationAuditEvent({ communication, outcome = "0", store }) {
  const senderRef = communication?.sender?.reference || "";
  const practitionerDisplay = communication?.sender?.display || "Unknown";

  return buildAuditEvent({
    action: "C",
    subtype: {
      system: "http://hl7.org/fhir/restful-interaction",
      code: "create",
      display: "Create"
    },
    agent: {
      who: {
        reference: senderRef,
        display: practitionerDisplay
      },
      requestor: true
    },
    entity: {
      what: {
        reference: `Communication/${communication?.id || "unknown"}`,
        display: communication?.topic?.text || "Message"
      },
      role: { code: "4", display: "Communication" }
    },
    description: "User sent a communication message",
    outcome,
    store
  });
}

/**
 * Build a FHIR AuditEvent for an error or failure event
 * @param {object} params - includes errorType, error, agent, entity, description, outcome, store
 * @returns {object} FHIR AuditEvent
 */
export function buildErrorAuditEvent({ errorType, error, agent, entity, description, outcome, store }) {
  let computedOutcome = "8";
  const status = error?.status || error?.response?.status;

  if (status) {
    if (status >= 400 && status < 500) computedOutcome = "4";
    else if (status >= 500) computedOutcome = "8";
  } else if (error?.severity === "minor") computedOutcome = "4";
  else if (error?.severity === "fatal") computedOutcome = "12";

  if (outcome) computedOutcome = outcome;

  const message =
    description ||
    error?.message ||
    "An unexpected error occurred during FHIR interaction.";

  const subtype = {
    system: "http://hl7.org/fhir/restful-interaction",
    code: errorType || "exception",
    display: "Error Event"
  };

  const resolvedAgent = agent || {
    who: { display: "System" },
    requestor: false
  };

  const resolvedEntity = entity
    ? entity
    : error?.resourceId
    ? {
        what: {
          reference: error.resourceId,
          display: "Related resource"
        }
      }
    : undefined;

  const auditEvent = buildAuditEvent({
    action: "E",
    subtype,
    agent: resolvedAgent,
    entity: resolvedEntity,
    description: message,
    outcome: computedOutcome,
    store
  });

  auditEvent.outcomeDesc = message;

  return auditEvent;
}